import { useTheme } from "arkh";
import { useCallback, useEffect, useMemo } from "react";
import { useQueryLoader, useRelayEnvironment } from "react-relay";

import { WIDGET_TEAM_SCHEDULE_MOCK_PREVIEW_QUERY } from "./WidgetTeamScheduleMockPreview";
import {
  WidgetOverviewSize,
  WidgetTeamScheduleMockPreviewQuery,
  WidgetTeamScheduleMockPreviewQuery$rawResponse,
  WidgetTeamScheduleMockPreviewQuery$variables,
} from "./__generated__/WidgetTeamScheduleMockPreviewQuery.graphql";
import { createOperationDescriptor, getRequest } from "relay-runtime";
import { DateTime } from "luxon";
import { useIntl } from "react-intl";
import { useIntlTokens } from "../../../../../../components/mobile/useIntlTokens";

const DATA_PREFIX = "WIDGET_ADD_TEAM_SCHEDULE";

function mockEvent({
  index,
  startAt,
  deadlineAt,
  title,
  description = "",
  issueCount,
  status,
}: {
  issueCount?: number;
  deadlineAt?: string;
  index: number;
  startAt: string;
  title: string;
  description?: string;
  status:
    | "IN_PROGRESS"
    | "NO_TASKS_COMPLETED"
    | "FUTURE"
    | "COMPLETED"
    | "EXPIRED";
}) {
  return {
    __typename: "RoutineOccasion" as const,
    deadlineAt: deadlineAt ?? null,
    id: DATA_PREFIX + "routineOccassion" + index,
    startAt,
    totalTaskExecutionCount: 4,
    __isTeamScheduleEventOccasion: "RoutineOccasion" as const,
    routine: {
      id: DATA_PREFIX + "routine" + index,
      title,
      description,
      descriptionImages: [],
      isOrg: false,
      orgIconUri: null,
    },
    status,
    expireAt: deadlineAt ?? DateTime.now().set({ hour: 24, minute: 0 }).toISO(),
    issueCount: issueCount ?? 0,
    sequenceNumber: 0,
    assigned: [],
    assignedRoles: [],
  } as const;
}

export function useWidgetTeamScheduleMockPreviewQueryLoader(props: {
  size: WidgetOverviewSize;
  storecastId: string;
}) {
  const env = useRelayEnvironment();
  const theme = useTheme();
  const vars: WidgetTeamScheduleMockPreviewQuery$variables = useMemo(
    () =>
      ({
        storecastId: props.storecastId,
        input: {
          teamIds: [],
          size: props.size,
          type: "DAILY_WITH_SUMMARY",
        },
      }) as const,
    [props.size, props.storecastId]
  );

  const [queryRef, load] = useQueryLoader<WidgetTeamScheduleMockPreviewQuery>(
    WIDGET_TEAM_SCHEDULE_MOCK_PREVIEW_QUERY
  );
  const intl = useIntl();
  const intl_tokens = useIntlTokens();

  const populateData = useCallback(() => {
    const req = getRequest(WIDGET_TEAM_SCHEDULE_MOCK_PREVIEW_QUERY);
    const op = createOperationDescriptor(req, vars);

    const payload: WidgetTeamScheduleMockPreviewQuery$rawResponse = {
      dashboard: {
        id: vars.storecastId,
        previewWidgetTeamSchedule: {
          id: DATA_PREFIX,
          title: intl_tokens.TeamSchedule,
          overviewSize: props.size,
          teams: [
            {
              id: DATA_PREFIX + "team",
              color: theme.primary,
            },
          ],
          content: {
            __typename: "WidgetTeamScheduleContentSummary" as const,
            weeklyTeamSchedule: {
              events: [
                {
                  id: "weekly-item-id-1",
                  __typename: "RoutineOccasion",
                  totalTaskExecutionCount: 1,
                  status: "COMPLETED" as const,
                },
                {
                  id: "weekly-item-id-2",
                  __typename: "RoutineOccasion",
                  totalTaskExecutionCount: 1,
                  status: "IN_PROGRESS" as const,
                },
              ],
              freq: "WEEKLY",
              id: "mock-preview-weekly-team-schedule",
              label: DateTime.now().toFormat("W").toString(),
            },
            monthlyTeamSchedule: {
              events: [
                {
                  id: "monthly-item-id-1",
                  __typename: "RoutineOccasion",
                  totalTaskExecutionCount: 5,
                  status: "IN_PROGRESS" as const,
                },
                {
                  id: "monthly-item-id-2",
                  __typename: "RoutineOccasion",
                  totalTaskExecutionCount: 5,
                  status: "IN_PROGRESS" as const,
                },
                {
                  id: "monthly-item-id-3",
                  __typename: "RoutineOccasion",
                  totalTaskExecutionCount: 5,
                  status: "COMPLETED" as const,
                },
              ],
              freq: "MONTHLY",
              id: "mock-preview-monthly-team-schedule",
              label: DateTime.now().toLocaleString({ month: "long" }),
            },
            dailyTeamSchedule: {
              id: "id 23",
              label: "Today",
              freq: "DAILY" as const,
              events: [
                mockEvent({
                  index: 1,
                  startAt: DateTime.now()
                    .set({ hour: 6, minute: 15 })
                    .toString(),
                  title: intl.formatMessage({
                    description: "WATeamSchedulePreviewTitle1",
                    defaultMessage: "Daily Checklist",
                  }),
                  description: intl.formatMessage({
                    description: "WATeamSchedulePreviewDescription1",
                    defaultMessage:
                      "Work through the Store Checklist and report issues for each department.",
                  }),
                  status: "COMPLETED",
                }),
                mockEvent({
                  index: 2,
                  startAt: DateTime.now()
                    .set({ hour: 6, minute: 30 })
                    .toString(),

                  title: intl.formatMessage({
                    description: "WATeamSchedulePreviewTitle2",
                    defaultMessage: "Pricing & Display",
                  }),
                  description: intl.formatMessage({
                    description: "WATeamSchedulePreviewDescription2",
                    defaultMessage:
                      "Make sure to check changes and updates for the week.",
                  }),
                  status: "COMPLETED",
                }),
                mockEvent({
                  index: 3,
                  startAt: DateTime.now()
                    .set({ hour: 7, minute: 0 })
                    .toString(),
                  title: intl.formatMessage({
                    description: "WATeamSchedulePreviewTitle3",
                    defaultMessage: "Loading dock",
                  }),
                  description: intl.formatMessage({
                    description: "WATeamSchedulePreviewDescription3",
                    defaultMessage: "Receive today's shipping of goods.",
                  }),
                  status: "COMPLETED",
                }),
                mockEvent({
                  index: 4,
                  startAt: DateTime.now()
                    .set({ hour: 7, minute: 15 })
                    .toString(),
                  title: intl.formatMessage({
                    description: "WATeamSchedulePreviewTitle4",
                    defaultMessage: "Promotions",
                  }),
                  description: intl.formatMessage({
                    description: "WATeamSchedulePreviewDescription4",
                    defaultMessage:
                      "Work through current promotional items and update in-store if needed.",
                  }),
                  status: "IN_PROGRESS",
                }),
                mockEvent({
                  index: 5,
                  startAt: DateTime.now()
                    .set({ hour: 9, minute: 0 })
                    .toString(),
                  title: intl.formatMessage({
                    description: "WATeamSchedulePreviewTitle5",
                    defaultMessage: "Standup",
                  }),
                  description: intl.formatMessage({
                    description: "WATeamSchedulePreviewDescription5",
                    defaultMessage:
                      "Gather everyone for a quick briefing and prioritise tasks for the day.",
                  }),
                  status: "IN_PROGRESS",
                }),
                mockEvent({
                  index: 6,
                  startAt: DateTime.now()
                    .set({ hour: 9, minute: 15 })
                    .toString(),
                  title: intl.formatMessage({
                    description: "WATeamSchedulePreviewTitle6",
                    defaultMessage: "Replenishment",
                  }),
                  description: intl.formatMessage({
                    description: "WATeamSchedulePreviewDescription6",
                    defaultMessage:
                      "Remember to greet customers with a smile. If time over make sure to help fix current issues.",
                  }),
                  status: "IN_PROGRESS",
                }),
                mockEvent({
                  index: 7,
                  startAt: DateTime.now()
                    .set({ hour: 12, minute: 0 })
                    .toString(),
                  title: intl.formatMessage({
                    description: "WATeamSchedulePreviewTitle7",
                    defaultMessage: "Lunch break",
                  }),
                  description: intl.formatMessage({
                    description: "WATeamSchedulePreviewDescription7",
                    defaultMessage:
                      "Stay on staff schedule during lunchtime for optimal staffing.",
                  }),
                  status: "IN_PROGRESS",
                }),
                mockEvent({
                  index: 8,
                  startAt: DateTime.now()
                    .set({ hour: 13, minute: 0 })
                    .toString(),
                  title: intl.formatMessage({
                    description: "WATeamSchedulePreviewTitle8",
                    defaultMessage: "Customer focus",
                  }),
                  description: intl.formatMessage({
                    description: "WATeamSchedulePreviewDescription8",
                    defaultMessage:
                      "Help customers and make sure to prioritise tasks that improve the customer experience",
                  }),
                  status: "IN_PROGRESS",
                }),
                mockEvent({
                  index: 9,
                  startAt: DateTime.now()
                    .set({ hour: 15, minute: 0 })
                    .toString(),
                  title: intl.formatMessage({
                    description: "WATeamSchedulePreviewTitle9",
                    defaultMessage: "Hand over",
                  }),
                  description: intl.formatMessage({
                    description: "WATeamSchedulePreviewDescription9",
                    defaultMessage:
                      "Brief the night shift staff on important information before you leave.",
                  }),
                  status: "IN_PROGRESS",
                }),
              ],
            } as const,
          },
        },
      },
    };
    env.commitPayload(op, payload);
    load(vars, {
      fetchPolicy: "store-only",
    });
  }, [
    env,
    intl,
    intl_tokens.TeamSchedule,
    load,
    props.size,
    theme.primary,
    vars,
  ]);

  useEffect(() => populateData(), [populateData]);

  return queryRef;
}
